import React, { useState } from "react";
import { Link } from "react-router-dom";
import Icon from "../components/Icon";
import { useData } from "../data/DataProvider";
import { getArchetype, classifyEngine } from "../data/archetypes";
import { roleColor } from "../data/roleColors";
import { isGeneralSupt } from "../services/roles";
import "../styles.css";

function getInitials(name) {
  return name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
}

// Roles offered in the quick-edit dropdown (alphabetical).
const ROLE_CHOICES = [
  "Sr. PM", "PM", "APM",
  "Sr. Supt", "Supt", "Finish Supt", "Asst. Supt", "Gen. Supt",
  "PE", "FE", "QA/QC Engineer",
  "PX", "Project Accountant", "Estimator", "Precon", "Director",
  "Admin", "Staff", "Intern", "Carpenter", "Expeditor",
].sort((a, b) => a.localeCompare(b));

// Fine-grained groups (checked in priority order). PE and QA/QC are separate.
// General Superintendent is leadership, NOT a field super.
// Word-boundary regexes for the short codes (PM/PE/FE) so they can't fire
// inside longer words — "carpenter"/"expeditor" used to match "pe" and land
// in Project Engineers.
function bucketOf(role) {
  const r = (role || "").toLowerCase();
  if (isGeneralSupt(role)) return "Leadership & Precon";
  if (/(sr\.?\s*pm\b|senior project manager)/.test(r)) return "Senior Project Managers";
  if (/(\bapm\b|assistant project manager)/.test(r)) return "Assistant Project Managers";
  if (/(\bpm\b|project manager)/.test(r)) return "Project Managers";
  if (/(sr\.?\s*supt|senior super)/.test(r)) return "Senior Superintendents";
  if (/finish\s*sup/.test(r)) return "Finish Superintendents";
  if (/(asst\.?\s*supt|assistant super)/.test(r)) return "Assistant Superintendents";
  if (/(supt|superintendent)/.test(r)) return "Superintendents";
  if (/(\bqa\b|\bqc\b|qa\/qc)/.test(r)) return "QA/QC Project Engineers";
  if (/(\bpe\b|\bfe\b|engineer)/.test(r)) return "Project Engineers";
  if (/(\bpx\b|director|\bvp\b|precon|ceo|cfo|evp|staff)/.test(r)) return "Leadership & Precon";
  if (/(accountant|admin|office|payroll|controller|\bhr\b|market|expeditor|carpenter|^it$)/.test(r)) return "Accounting & Office";
  return "Other";
}
// Mirrors POSITION_ORDER in services/roles.js so People and Assignments group
// the same way (Sr / Supt / Finish / Asst superintendents each broken out).
const GROUP_ORDER = [
  "Senior Project Managers", "Project Managers", "Assistant Project Managers",
  "Senior Superintendents", "Superintendents", "Finish Superintendents", "Assistant Superintendents",
  "Project Engineers", "QA/QC Project Engineers",
  "Leadership & Precon", "Accounting & Office", "Other",
];

function TraitRow({ letter, survey, job }) {
  const s = survey ?? 0;
  const j = job ?? survey ?? 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 7 }}>
      <span style={{ width: 18, height: 18, borderRadius: 5, background: "var(--bg-elevated)", color: "var(--text-secondary)", fontSize: 10, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{letter}</span>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
        <div style={{ height: 5, background: "var(--bg-elevated)", borderRadius: 3, overflow: "hidden" }}>
          <div style={{ width: `${(s / 10) * 100}%`, height: "100%", background: "var(--gold)", borderRadius: 3 }} />
        </div>
        <div style={{ height: 5, background: "var(--bg-elevated)", borderRadius: 3, overflow: "hidden" }}>
          <div style={{ width: `${(j / 10) * 100}%`, height: "100%", background: "var(--navy-mid)", borderRadius: 3 }} />
        </div>
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, width: 64, textAlign: "right", whiteSpace: "nowrap" }}>
        <span style={{ color: "var(--gold-text)" }}>S {survey ?? "–"}</span>
        <span style={{ color: "var(--text-muted)" }}> / </span>
        <span style={{ color: "var(--navy-mid)" }}>J {job ?? "–"}</span>
      </span>
    </div>
  );
}

function PersonCard({ person, isAdmin, editPerson }) {
  const arch = getArchetype(person.Archetype);
  const [editing, setEditing] = useState(false);
  const [role, setRole] = useState(person.CompanyRole || "");
  const [saving, setSaving] = useState(false);

  const startEdit = (e) => { e.preventDefault(); e.stopPropagation(); setRole(person.CompanyRole || ""); setEditing(true); };
  const cancel = (e) => { e.preventDefault(); e.stopPropagation(); setEditing(false); };
  const save = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setSaving(true);
    try { await editPerson(person.Id, { CompanyRole: role }); setEditing(false); }
    finally { setSaving(false); }
  };

  const roleOptions = (ROLE_CHOICES.includes(person.CompanyRole) || !person.CompanyRole)
    ? ROLE_CHOICES : [person.CompanyRole, ...ROLE_CHOICES];

  return (
    <Link to={`/people/${person.Id}`} className="person-card" style={{ position: "relative" }}>
      {isAdmin && !editing && (
        <button onClick={startEdit} title="Edit role"
          style={{ position: "absolute", top: 10, right: 10, background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: 6, fontSize: 11, padding: "3px 8px", cursor: "pointer", color: "var(--text-secondary)", zIndex: 2 }}>
          Edit role
        </button>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <div className="avatar" style={{ width: 46, height: 46, fontSize: 15, flexShrink: 0, background: roleColor(person.CompanyRole) }}>
          {getInitials(person.FullName)}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{person.FullName}</div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 4 }}>{person.CompanyRole}</div>
          <span className="person-archetype" style={{ margin: 0, display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11 }}>
            <Icon name={arch.icon} size={12} /> {person.Archetype || "No Profile"}
          </span>
        </div>
      </div>

      {editing && (
        <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} style={{ marginBottom: 14, padding: 10, background: "var(--bg-elevated)", borderRadius: 8, border: "1px solid var(--gold)" }}>
          <div style={{ fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Company Role</div>
          <select className="form-select" value={role} onChange={e => setRole(e.target.value)} style={{ marginBottom: 8 }}>
            {roleOptions.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-primary" style={{ fontSize: 12, padding: "4px 12px" }} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
            <button className="btn btn-ghost" style={{ fontSize: 12, padding: "4px 12px" }} onClick={cancel} disabled={saving}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>
        Culture Index traits (0–10)
      </div>
      {["A", "B", "C", "D"].map(t => (
        <TraitRow key={t} letter={t} survey={person[`Survey_${t}`]} job={person[`Job_${t}`]} />
      ))}

      <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--border)", display: "flex", gap: 16 }}>
        <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>Energy <b style={{ color: "var(--text-primary)", fontWeight: 800 }}>{person.Survey_EU}</b></span>
        <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>Logic <b style={{ color: "var(--text-primary)", fontWeight: 800 }}>{person.Survey_L}</b></span>
        <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>Ingenuity <b style={{ color: "var(--text-primary)", fontWeight: 800 }}>{person.Survey_I}</b></span>
      </div>
    </Link>
  );
}

export default function PeopleDirectory() {
  const { people, loading, error, isAdmin, editPerson } = useData();
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const toggle = (g) => setExpanded((e) => ({ ...e, [g]: !e[g] }));

  // Export a simple Name + Position roster as CSV (opens straight into Excel).
  const exportRoster = () => {
    const rows = (people || [])
      .filter(p => p.IsActive !== false)
      .sort((a, b) => (a.FullName || "").localeCompare(b.FullName || ""))
      .map(p => [p.FullName || "", p.CompanyRole || ""]);
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = "﻿" + [["Name", "Position"], ...rows].map(r => r.map(esc).join(",")).join("\r\n"); // BOM so Excel reads UTF-8
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Hardaway-Roster-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="page"><div className="empty"><div className="spinner" /><div className="empty-text" style={{ marginTop: 16 }}>Loading people…</div></div></div>;
  if (error) return <div className="page"><div className="empty"><div className="empty-text">Couldn't load data: {error}</div></div></div>;

  const q = search.toLowerCase();
  const searching = q.length > 0;
  const filtered = people.filter(p =>
    p.FullName.toLowerCase().includes(q) || (p.Archetype || "").toLowerCase().includes(q) || (p.CompanyRole || "").toLowerCase().includes(q)
  );

  const groups = {};
  filtered.forEach(p => { const b = bucketOf(p.CompanyRole); (groups[b] = groups[b] || []).push(p); });
  Object.values(groups).forEach(arr => arr.sort((a, b) => a.FullName.localeCompare(b.FullName)));
  const orderedGroups = GROUP_ORDER.filter(g => groups[g]?.length);

  const engine = { gas: 0, brake: 0, glue: 0 };
  people.forEach(p => { const e = classifyEngine(p); if (engine[e] != null) engine[e]++; });

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">People</div>
          <div className="page-subtitle">{people.length} profiles · grouped by role (click a group to expand){isAdmin ? " · admins can edit roles" : ""}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button className="btn btn-secondary" onClick={exportRoster} title="Download a Name + Position roster (opens in Excel)"><Icon name="download" size={14} style={{ verticalAlign: "middle", marginRight: 5 }} /> Export roster</button>
          <Link to="/admin/import" className="btn btn-primary">+ Add People</Link>
        </div>
      </div>

      <div className="wp-engine-strip" style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px", color: "var(--text-muted)" }}>Organizational Behavioral Balance</div>
        <div className="wp-engine-pills">
          <div className="wp-eng-pill gas"><Icon name="up" size={13} /> {engine.gas} Gas — Drivers</div>
          <div className="wp-eng-pill brake"><Icon name="anchor" size={13} /> {engine.brake} Brake — Stabilizers</div>
          <div className="wp-eng-pill glue"><Icon name="users" size={13} /> {engine.glue} Glue — Connectors</div>
        </div>
        <Link to="/hiring" style={{ fontSize: 12, color: "var(--gold-text)", fontWeight: 600, marginLeft: "auto", textDecoration: "none" }}>View Org Gaps →</Link>
      </div>

      <div className="flex items-center gap-12 mb-16" style={{ flexWrap: "wrap" }}>
        <input className="form-input" style={{ maxWidth: 320 }} placeholder="Search name, role, or archetype…" value={search} onChange={e => setSearch(e.target.value)} />
        <div style={{ display: "flex", gap: 14, fontSize: 11, color: "var(--text-secondary)" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 14, height: 5, background: "var(--gold)", borderRadius: 3 }} /> S = Survey (natural)</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 14, height: 5, background: "var(--navy-mid)", borderRadius: 3 }} /> J = On-Job</span>
        </div>
      </div>

      {orderedGroups.length === 0 ? (
        <div className="empty"><div className="empty-icon">👷</div><div className="empty-text">No people match your search.</div></div>
      ) : (
        orderedGroups.map(group => {
          const open = searching || expanded[group];
          return (
            <div key={group} style={{ marginBottom: 14 }}>
              <div onClick={() => toggle(group)} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "10px 14px", background: "var(--bg-elevated)", borderRadius: "var(--radius)", userSelect: "none" }}>
                <span style={{ fontSize: 12, color: "var(--text-muted)", width: 12 }}>{open ? "▾" : "▸"}</span>
                <span className="section-title" style={{ fontSize: 15 }}>{group}</span>
                <span style={{ color: "var(--text-muted)", fontWeight: 600, fontSize: 13 }}>· {groups[group].length}</span>
              </div>
              {open && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 16, marginTop: 14 }}>
                  {groups[group].map(p => <PersonCard key={p.Id} person={p} isAdmin={isAdmin} editPerson={editPerson} />)}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
